import { TIMEOUT_NONE } from '@/api/timeouts'
import { uploadFile } from '@/config/utils'
import { isDemoDeployment } from '@/utils/deployment'
import { isHeavyTransferInProgress } from '@/utils/transferLock'
import type { AxiosRequestConfig } from 'axios'
import { apiCall } from './axiosInstance'
import { firmwaresApi as demoFirmwaresApi } from './demo/firmwaresApi'

/**
 * Relaxes a request's timeout only while a firmware/file upload or download is actually in
 * flight (see transferLock.ts) — that's the one case where a concurrent transfer can starve this
 * request's bandwidth past the default client timeout. Otherwise keeps the default timeout so a
 * genuinely broken request still fails promptly instead of hanging forever.
 */
const contentionAwareConfig = async (config: AxiosRequestConfig = {}): Promise<AxiosRequestConfig> => {
	const contended = await isHeavyTransferInProgress()
	return contended ? { ...config, timeout: TIMEOUT_NONE } : config
}

export type FirmwareVersionDependency = {
	component_name: string
	min_version: string
}
export interface FirmwareInBundle {
	component_name: string
	version_number: string
	file_name: string
	is_deactivated: boolean
	checksum: string
	content_length: number
	uncompressed_size: number | null
	firmware_id: string
	dependencies: FirmwareVersionDependency[]
}

export interface FirmwareBundle {
	id: string
	version: string
	file_name: string
	firmwares: FirmwareInBundle[]
	upload_status: string
	content_length: number
	created_at: Date
	created_by: string
	checksum: string
	uncompressed_size: number
}

export interface FirmwareBundleResult {
	results: FirmwareBundle[]
}

export interface FirmwareType {
	version: string
	file_name: string
	content_length: number
	checksum: string
	uncompressed_size: number | null
	created_at: string
	created_by: string
}

export interface UploadFirmwareResponse {
	status: string
	version: string
}

export const v3FirmwaresApi = {
	getFirmwares: async (): Promise<FirmwareType[]> => {
		const response = await apiCall<{ firmwares: FirmwareType[] }>('/v3/firmwares', await contentionAwareConfig())
		return response.firmwares ?? []
	},
	getFiles: async (): Promise<FirmwareType[]> => {
		const response = await apiCall<{ firmwares: FirmwareType[] }>('/v3/files', await contentionAwareConfig())
		return response.firmwares ?? []
	},
	uploadFirmware: async (
		version: string,
		formData: FormData,
		onUploadProgress?: (progress: { loaded: number; total: number }) => void,
		signal?: AbortSignal,
	): Promise<UploadFirmwareResponse> => {
		const res = (await uploadFile(
			`/v3/firmwares/${encodeURIComponent(version)}`,
			formData,
			onUploadProgress ? p => onUploadProgress({ loaded: p.loaded, total: p.total ?? 0 }) : () => {},
			signal,
		)) as { data: UploadFirmwareResponse }
		return res.data
	},
	updateFirmwareMetadata: async (version: string, uncompressed_size: number): Promise<void> =>
		apiCall(
			`/v3/firmwares/${encodeURIComponent(version)}`,
			await contentionAwareConfig({
				method: 'PATCH',
				data: {
					uncompressed_size,
				},
			}),
		),
	deleteFirmware: async (version: string): Promise<void> =>
		apiCall(`/v3/firmwares/${encodeURIComponent(version)}`, await contentionAwareConfig({ method: 'DELETE' })),
	deleteFile: async (version: string): Promise<void> =>
		apiCall(`/v3/files/${encodeURIComponent(version)}`, await contentionAwareConfig({ method: 'DELETE' })),
	downloadFile: async (version: string): Promise<Blob> =>
		apiCall<Blob>(`/v3/files/${encodeURIComponent(version)}/download`, {
			responseType: 'blob',
			timeout: TIMEOUT_NONE,
		}),
	downloadFirmware: async (version: string): Promise<Blob> =>
		apiCall<Blob>(`/v3/firmwares/${encodeURIComponent(version)}/download`, {
			responseType: 'blob',
			timeout: TIMEOUT_NONE,
		}),
}

export const firmwaresApi = isDemoDeployment() ? demoFirmwaresApi : v3FirmwaresApi
